import type { ComponentProps, FunctionComponent } from 'react';
import { useSuburbPageQuery } from '../../graphql/useSuburbPageQuery';
import { ContentBrick } from '../../../app-kit/components/ContentBrick/ContentBrick';
import styled from 'styled-components';
import { trackDisclaimerModalOpen } from '../../../analytics/event-tracking/trackDisclaimerModalOpen';
import { DisclaimerIcon } from '../../../app-kit/components/DisclaimerIcon/DisclaimerIcon';
import { useSuburbPageModalContext } from '../../contexts/SuburbPageModalContext/SuburbPageModalContext';
import {
  mediaHelper,
  paletteHelper,
  spacingHelper,
  Text,
  typographyHelper,
} from '@property.com.au/ui-kit';
import { DemographicsTile } from './subcomponents/DemographicsTile';
import { AgeGroupsSubBrick } from './subcomponents/AgeGroupsSubBrick/AgeGroupsSubBrick';
import { HouseholdsSubBrick } from './subcomponents/HouseholdsSubBrick/HouseholdsSubBrick';
import { OwnershipSubBrick } from './subcomponents/OwnershipSubBrick';
import { DynamicText, TextWithStyles } from '../../../app-kit/components/DynamicText/DynamicText';

const Population = styled(DemographicsTile)`
  grid-area: population;
`;

const Income = styled(DemographicsTile)`
  grid-area: income;
`;

const Age = styled(AgeGroupsSubBrick)`
  grid-area: age;
`;

const Households = styled(HouseholdsSubBrick)`
  grid-area: households;
`;

const Ownership = styled(OwnershipSubBrick)`
  grid-area: ownership;
`;

const Grid = styled.div`
  display: grid;
  gap: ${spacingHelper('extraSmall')};
  grid-template-areas: 'population income' 'age age' 'households households' 'ownership ownership';
  grid-template-columns: 1fr 1fr;

  ${mediaHelper('desktopMin')} {
    grid-template-areas: 'population income' 'age households' 'ownership ownership';
  }
`;

const DisclaimerText = styled(Text)`
  ${typographyHelper('caption')};
  color: ${paletteHelper('textSecondary')};
  margin-top: ${spacingHelper('extraSmall')};
`;

export type SuburbPageDemographicsBrickProps = {
  title: string;
  subtitle: TextWithStyles[];
  disclaimer: string;
  populationTile: ComponentProps<typeof DemographicsTile>;
  incomeTile: ComponentProps<typeof DemographicsTile>;
  ageGroupsSubBrick: ComponentProps<typeof AgeGroupsSubBrick>;
  householdsSubBrick: ComponentProps<typeof HouseholdsSubBrick>;
  ownershipSubBrick: ComponentProps<typeof OwnershipSubBrick>;
};

export const SuburbPageDemographicsBrick: FunctionComponent<SuburbPageDemographicsBrickProps> = ({
  title,
  subtitle,
  disclaimer,
  populationTile,
  incomeTile,
  ageGroupsSubBrick,
  householdsSubBrick,
  ownershipSubBrick,
}) => {
  const { displayModal } = useSuburbPageModalContext();
  return (
    <ContentBrick
      title={title}
      subtitleComponent={
        <DynamicText
          dynamicTexts={subtitle}
          variant="body01"
          variantBold="body01Bold"
          colorVariant="textSecondary"
        />
      }
      headerChildren={
        <DisclaimerIcon
          title="Open Demographics Data Disclaimer"
          onClick={() => {
            displayModal({ name: 'demographicsDataDisclaimer' });
            trackDisclaimerModalOpen({
              inPageSource: 'suburb_page__suburb_demographics_brick___disclaimer_button',
            });
          }}
        />
      }
    >
      <Grid>
        <Population {...populationTile} />
        <Income {...incomeTile} />
        <Age {...ageGroupsSubBrick} />
        <Households {...householdsSubBrick} />
        <Ownership {...ownershipSubBrick} />
      </Grid>
      <DisclaimerText>{disclaimer}</DisclaimerText>
    </ContentBrick>
  );
};

export const SuburbPageDemographicsBrickWithData: FunctionComponent = () => {
  const demographics = useSuburbPageQuery()?.data?.suburbPageBySlug?.demographics;

  if (!demographics) return null;

  const {
    title,
    subtitle,
    disclaimer,
    populationTile,
    incomeTile,
    ageGroupsSubBrick,
    householdsSubBrick,
    propertyOwnershipSubBrick,
  } = demographics;

  return (
    <SuburbPageDemographicsBrick
      title={title}
      subtitle={subtitle}
      disclaimer={disclaimer}
      populationTile={populationTile}
      incomeTile={incomeTile}
      ageGroupsSubBrick={ageGroupsSubBrick}
      householdsSubBrick={householdsSubBrick}
      ownershipSubBrick={propertyOwnershipSubBrick}
    />
  );
};
